import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useI18n } from "@/i18n/I18nContext";
import { getLaunchRemaining } from "@/lib/launch";

const DISMISS_KEY = "m2i_top_bar_dismissed";

export default function TopCountdownBar({ onBuy }) {
  const { t } = useI18n();
  const [visible, setVisible] = useState(false);
  const [tick, setTick] = useState(() => getLaunchRemaining());

  // Scroll listener — reveal once bottom of pricing section passes the viewport top
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (sessionStorage.getItem(DISMISS_KEY) === "1") return;

    const check = () => {
      if (sessionStorage.getItem(DISMISS_KEY) === "1") {
        setVisible(false);
        return;
      }
      const r = getLaunchRemaining();
      if (r.ended) {
        setVisible(false);
        return;
      }
      const el = document.querySelector("#pricing");
      if (!el) return;
      const rect = el.getBoundingClientRect();
      // Show once pricing section has mostly scrolled past the top of the viewport
      setVisible(rect.bottom < 80);
    };

    check();
    window.addEventListener("scroll", check, { passive: true });
    window.addEventListener("resize", check);
    return () => {
      window.removeEventListener("scroll", check);
      window.removeEventListener("resize", check);
    };
  }, []);

  // Live-tick
  useEffect(() => {
    if (!visible) return;
    const id = setInterval(() => setTick(getLaunchRemaining()), 1000);
    return () => clearInterval(id);
  }, [visible]);

  // Push the header down with a class on the <html> element while bar is open
  useEffect(() => {
    const root = document.documentElement;
    if (visible) root.classList.add("has-top-bar");
    else root.classList.remove("has-top-bar");
    return () => root.classList.remove("has-top-bar");
  }, [visible]);

  if (tick.ended) return null;

  const mins = String(tick.m).padStart(2, "0");
  const secs = String(tick.s).padStart(2, "0");

  const dismiss = () => {
    sessionStorage.setItem(DISMISS_KEY, "1");
    setVisible(false);
  };

  const handleBuy = () => {
    onBuy?.();
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ y: -48, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: -48, opacity: 0 }}
          transition={{ duration: 0.4, ease: [0.2, 0.8, 0.2, 1] }}
          className="fixed top-0 inset-x-0 z-[55] h-9 md:h-10 bg-[var(--marigold)] text-[var(--cream)] overflow-hidden"
          data-testid="top-countdown-bar"
        >
          <div className="container-x h-full flex items-center justify-center gap-3 md:gap-5 text-[12.5px] md:text-[13.5px]">
            <span className="relative hidden sm:flex h-2 w-2 shrink-0">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[var(--cream)] opacity-70" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-[var(--cream)]" />
            </span>

            <span className="font-mono-caption tracking-[0.16em] uppercase text-[10px] md:text-[10.5px] opacity-80 hidden md:inline">
              {t.top_bar.label}
            </span>
            <span className="font-display tracking-tight text-[16px] md:text-[18px] leading-none tabular-nums">
              {mins}:{secs}
            </span>
            <span className="hidden sm:inline opacity-85">{t.top_bar.hint}</span>

            <button
              onClick={handleBuy}
              className="font-medium underline underline-offset-4 decoration-[rgba(253,251,247,0.5)] hover:decoration-[var(--cream)] transition-colors shrink-0"
              data-testid="top-bar-buy"
            >
              {t.top_bar.cta}
            </button>

            <button
              onClick={dismiss}
              aria-label={t.top_bar.dismiss}
              className="w-6 h-6 rounded-full hover:bg-[rgba(15,31,23,0.15)] flex items-center justify-center shrink-0"
              data-testid="top-bar-close"
            >
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none">
                <path d="M6 6l12 12M18 6l-12 12" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"/>
              </svg>
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
